import React, { useState, useMemo } from 'react';
import Fuse from "fuse.js";

const AnnotationsPanel = ({ annotations = [], isVisible, onToggle }) => {
  const [selectedAnnotationType, setSelectedAnnotationType] = useState('all');
  const [expandedDay, setExpandedDay] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const flatAnnotations = useMemo(() => Object.values(annotations).flat(), [annotations])

  const fuse = useMemo(
    () =>
      new Fuse(flatAnnotations, {
        keys: ["text"],
        threshold: 0,
        includeScore: true,
      }),
    [flatAnnotations],
  );

  const searchResults = useMemo(() => {
    if (!searchTerm) return flatAnnotations;
    return fuse.search(searchTerm).map((result) => result.item);
  }, [fuse, searchTerm]);


  // Group annotations by type and day
  const groupedAnnotations = useMemo(() => {
    const groups = {
      byType: {},
      byDay: {}
    };

    Object.values(searchResults).forEach(annotation => {

        if (!groups.byType[annotation.type])
            groups.byType[annotation.type] = [];
        groups.byType[annotation.type].push(annotation);

      // Group by day

        const days = Math.floor(annotation.minutesFromEvent / 1440);
        const dayKey = days === 0 ? 'Event Day' : 
                   days < 0 ? `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} before` :
                   `${days} day${days === 1 ? '' : 's'} after`;
      
        if (!groups.byDay[dayKey]) {
            groups.byDay[dayKey] = [];
        }
        groups.byDay[dayKey].push(annotation);

    });

    return groups;
  }, [searchResults]);


  const formatTimeFromEvent = (minutesFromEvent) => {
    const hours = Math.abs(minutesFromEvent) / 60;
    const sign = minutesFromEvent < 0 ? '' : '+';
    if (hours < 1) {
      return `${sign}${Math.abs(minutesFromEvent)}m`;
    } else if (hours < 24) {
      return `${sign}${hours.toFixed(1)}h`;
    } else {
      return `${sign}${(hours / 24).toFixed(1)}d`;
    }
  };

  const getAnnotationIcon = (type) => {
    switch (type.toLowerCase()) {
      case 'bathing': return '🚿';
      case 'entertainment': return '🎮';
      case 'excercise': return '🏃';
      case 'exercise': return '🏃'; // Handle potential spelling variation
      case 'meals': return '🍽️';
      case 'medication': return '💊';
      case 'other activity': return '🔧';
      case 'therapy': return '⚕️';
      case 'toileting': return '🚻';
      case 'note': return '📝';
      default: return '📌';
    }
  };

  const getAnnotationColor = (type) => {
    switch (type.toLowerCase()) {
      case 'bathing': return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'entertainment': return 'bg-pink-100 text-pink-800 border-pink-200';
      case 'excercise': 
      case 'exercise': return 'bg-green-100 text-green-800 border-green-200';
      case 'meals': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medication': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'other activity': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'therapy': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'toileting': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'note': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  if (annotations.length === 0) {
    return null;
  }

  return (
    <div className={`${isVisible ? 'lg:w-1/3' : 'lg:w-12'} transition-all duration-300`}>
      <div className="bg-white rounded-lg shadow-md border border-gray-200 h-fit sticky top-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <h3 className={`font-semibold text-gray-800 ${!isVisible && 'hidden'}`}>
              Annotations ({Object.values(annotations).flatMap(a => a.length).reduce((a, b) => a + b)})
            </h3>
            <span className="text-sm text-gray-500">📝</span>
          </div>
          <button
            onClick={onToggle}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title={isVisible ? 'Minimize' : 'Expand'}
          >
            {isVisible ? '−' : '+'}
          </button>
        </div>

        {isVisible && (
          <>
            <input id="search" className="gorder-gray-200 w-full p-2 mb-1" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} type="text" />
            {/* Filter Controls */}
            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setSelectedAnnotationType('all')}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    selectedAnnotationType === 'all'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  All ({Object.values(annotations).flatMap(a => a.length).reduce((a, b) => a + b)})
                </button>
                {Object.entries(groupedAnnotations.byType).map(([type, items]) => (
                  <button
                    key={type}
                    onClick={() => setSelectedAnnotationType(type)}
                    className={`px-3 py-1 text-xs rounded-full transition-colors ${
                      selectedAnnotationType === type
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {getAnnotationIcon(type)} {type} ({items.length})
                  </button>
                ))}
              </div>
            </div>

            {/* Annotations List */}
            <div className="max-h-96 overflow-y-auto">
              {Object.entries(groupedAnnotations.byDay)
                .sort(([a], [b]) => {
                  // Sort by day (most recent first)
                  const aValue = a === 'Event Day' ? 0 : 
                                a.includes('before') ? -parseInt(a.split(' ')[0]) :
                                parseInt(a.split(' ')[0]);
                  const bValue = b === 'Event Day' ? 0 : 
                                b.includes('before') ? -parseInt(b.split(' ')[0]) :
                                parseInt(b.split(' ')[0]);
                  return bValue - aValue;
                })
                .map(([dayKey, dayAnnotations]) => {
                  const filteredDayAnnotations = dayAnnotations.filter(a => 
                    selectedAnnotationType === 'all' || a.type === selectedAnnotationType
                  );
                  
                  if (filteredDayAnnotations.length === 0) return null;

                  return (
                    <div key={dayKey} className="border-b border-gray-100">
                      <button
                        onClick={() => setExpandedDay(expandedDay === dayKey ? null : dayKey)}
                        className="w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between transition-colors"
                      >
                        <span className="font-medium text-sm text-gray-700">
                          {dayKey} ({filteredDayAnnotations.length})
                        </span>
                        <span className="text-gray-400">
                          {expandedDay === dayKey ? '−' : '+'}
                        </span>
                      </button>
                      
                      {expandedDay === dayKey && (
                        <div className="px-3 pb-3 space-y-2">
                          {filteredDayAnnotations
                            .sort((a, b) => a.minutesFromEvent - b.minutesFromEvent)
                            .map((annotation, idx) => (
                            <div
                               key={`${annotation.date}-${idx}`}
                              className={`p-2 rounded border text-xs ${getAnnotationColor(annotation.type)}`}
                            >
                              <div className="flex items-start justify-between gap-2 mb-1">
                                <span className="font-medium">
                                  {getAnnotationIcon(annotation.type)} {annotation.type}
                                </span>
                                <span className="text-xs opacity-75">
                                  {formatTimeFromEvent(annotation.minutesFromEvent)}
                                </span>
                              </div>
                              <p className="text-gray-700 leading-relaxed">
                                {annotation.text}
                              </p>
                              {annotation.date && (
                                <div className="text-xs opacity-75 mt-1">
                                  {new Date(annotation.date).toLocaleString()}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AnnotationsPanel;